import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { addError, addWarning } from 'actions/Error';
import { Customer } from 'types/Customer';
import { RootState } from 'reducers';
import { publish } from 'services/CentrifugoWS';

import { startNewSaleCentrifugoMessage } from '../constants';
import { ProcessingCustomerState } from '../types';

import {
  getCustomerDisplayState,
  getFieldsToShow,
  getChannel,
  getNeedsStartNew,
} from './reducer';
import {
  SET_FETCHED_INITIAL_SCREEN_DETAILS,
  SET_HAS_PERMISSION,
  SET_SCREENS,
  SET_SELECTED_SCREEN,
  SET_NEEDS_START_NEW,
  SET_PROCESSING_CUSTOMER,
  SET_NEED_TO_CLOSE_CUSTOMER_FORM_ON_DISPLAY,
  RESET_CUSTOMER_FORM_STATE,
} from './actionTypes';

export const setHasPermission = value => ({
  type: SET_HAS_PERMISSION,
  payload: value,
});

export const setProcessingCustomer = (value: ProcessingCustomerState) => ({
  type: SET_PROCESSING_CUSTOMER,
  payload: value,
});

export const setNeedToCloseCustomerFormOnDisplay = (value: boolean) => ({
  type: SET_NEED_TO_CLOSE_CUSTOMER_FORM_ON_DISPLAY,
  payload: value,
});

export const resetCustomerFormState = () => ({
  type: RESET_CUSTOMER_FORM_STATE,
});

export const setFetchedInitialScreenDetails = value => ({
  type: SET_FETCHED_INITIAL_SCREEN_DETAILS,
  payload: value,
});

export const setScreens = screens => ({
  type: SET_SCREENS,
  payload: screens,
});

export const setSelectedScreen = screen => ({
  type: SET_SELECTED_SCREEN,
  payload: screen,
});

export const setNeedsStartNew = (needsStartNew: boolean) => ({
  type: SET_NEEDS_START_NEW,
  payload: needsStartNew,
});

const typedWindow = window as Window & {
  [key: string]: any;
  screen: Screen & { isExtended?: boolean };
};

export const getScreenDetails = () => async (dispatch, getState) => {
  const { hasPermission } = getCustomerDisplayState(getState());
  if (hasPermission !== null && !hasPermission) return null;
  try {
    if (!('getScreenDetails' in typedWindow)) {
      dispatch(setHasPermission(false));
      dispatch(
        addError('Multi-screen window open is not supported by your browser'),
      );
      return null;
    }

    const details = await typedWindow.getScreenDetails();
    if (hasPermission === null) dispatch(setHasPermission(true));
    dispatch(setScreens(details.screens));
    dispatch(setSelectedScreen(null));
    return details;
  } catch (error) {
    if (hasPermission === null) {
      dispatch(setHasPermission(false));
      dispatch(addError('Permission to detect multiple screens denied'));
      return null;
    }
    throw error;
  }
};

export const openWindowOnSelectedScreen = url => async (dispatch, getState) => {
  const { selectedScreen } = getCustomerDisplayState(getState());

  if (!selectedScreen) {
    dispatch(addWarning('Screen not selected. Opening on current screen.'));
    return window.open(url);
  }

  return window.open(
    url,
    undefined,
    `
      left=${selectedScreen.screen.left}
      top=${selectedScreen.screen.top}
      width=${selectedScreen.screen.width}
      height=${selectedScreen.screen.height}
    `,
  );
};

export const updateCustomerInformation = (customer: Customer) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const channel = getChannel(getState());

  if (!channel) {
    dispatch(addError('Channel name not configured!'));
    return;
  }
  const customerFieldsForDisplay = getFieldsToShow(getState());

  type FieldType = typeof customerFieldsForDisplay[0];

  // Attach existing information to the fields
  const fieldsToReturn: (FieldType & {
    initialValue: string;
  })[] = customerFieldsForDisplay.map(field => {
    const { name } = field;
    const initialValue =
      (name === 'zip' ? customer.addresses?.[0]?.postalCode : customer[name]) ??
      '';
    return {
      ...field,
      initialValue,
    };
  });

  const message = {
    action: 'UPDATE_CUSTOMER',
    fields: fieldsToReturn,
    customerId: customer.customerID,
  };

  if (getNeedsStartNew(getState())) {
    dispatch(setNeedsStartNew(false));
    publish({ channel, message: startNewSaleCentrifugoMessage });
  }

  publish({ channel, message });
};
